import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { MOCK_ORDERS, MockOrder } from '../mock-data/mock-orders.data';

/**
 * Global Search Example
 * Demonstrates global search functionality with debounce
 */
@Component({
  selector: 'app-search-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Búsqueda Global</h2>
        <p class="text-gray-600 mb-2">
          Búsqueda en tiempo real con debounce a través de todas las columnas visibles.
          Escribe cualquier término para filtrar los datos.
        </p>
        <div class="flex gap-2 flex-wrap">
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            Búsqueda Global
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            Debounce 300ms
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            Múltiples Columnas
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            Contador de Resultados
          </span>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          [data]="orders"
          [columnDefs]="columnDefs"
          [config]="gridConfig">
        </app-smart-grid>
      </div>

      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">💡 Cómo usar la búsqueda:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li>Escribe cualquier término en el campo de búsqueda</li>
          <li>La búsqueda se aplica automáticamente después de 300ms (debounce)</li>
          <li>Busca en todas las columnas visibles del grid</li>
          <li>Puedes buscar por número de documento, cliente, ciudad, clase, etc.</li>
          <li>Haz clic en la X para limpiar la búsqueda</li>
          <li>El contador muestra cuántos resultados coinciden</li>
          <li>La búsqueda se combina con filtros de columna y ordenamiento</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">🔍 Ejemplos de búsqueda:</h3>
        <div class="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span class="font-semibold">Buscar por cliente:</span>
            <code class="ml-2 px-2 py-1 bg-white dark:bg-gray-800 rounded">Comercial</code>
          </div>
          <div>
            <span class="font-semibold">Buscar por ciudad:</span>
            <code class="ml-2 px-2 py-1 bg-white dark:bg-gray-800 rounded">Tegucigalpa</code>
          </div>
          <div>
            <span class="font-semibold">Buscar por documento:</span>
            <code class="ml-2 px-2 py-1 bg-white dark:bg-gray-800 rounded">PED-2025</code>
          </div>
          <div>
            <span class="font-semibold">Buscar por clase:</span>
            <code class="ml-2 px-2 py-1 bg-white dark:bg-gray-800 rounded">Urgente</code>
          </div>
        </div>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📝 Código de este ejemplo:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{exampleCode}}</code></pre>
      </div>
    </div>
  `,
})
export class SearchExampleComponent {
  orders: MockOrder[] = MOCK_ORDERS;

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nro. Documento',
      width: 180,
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 250,
    },
    {
      field: 'class.name',
      headerName: 'Clase',
      width: 180,
    },
    {
      field: 'soldTo.address.city',
      headerName: 'Ciudad',
      width: 150,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 150,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    pagination: {
      enabled: true,
      pageSize: 20,
    },
    sorting: {
      enabled: true,
      multiColumn: true,
    },
    search: {
      enabled: true,              // ✨ Enable global search
      showSearchInput: true,      // Show search input
      debounceTime: 300,          // Debounce time in ms
      placeholder: 'Buscar pedidos...', // Custom placeholder
      caseSensitive: false,       // Case insensitive search
      searchPosition: 'top',      // Position at top
    },
    styling: {
      maxHeight: '600px',
      stripedRows: true,
    },
  };

  exampleCode = `// Component Code
columnDefs: ColDef<MockOrder>[] = [
  { field: 'serial', headerName: 'Nro. Documento' },
  { field: 'soldTo.name', headerName: 'Cliente' },
  { field: 'class.name', headerName: 'Clase' },
  { field: 'soldTo.address.city', headerName: 'Ciudad' }
];

gridConfig: GridConfig<MockOrder> = {
  search: {
    enabled: true,              // ✨ Enable global search
    showSearchInput: true,      // Show search input
    debounceTime: 300,          // Debounce 300ms
    placeholder: 'Buscar pedidos...',
    caseSensitive: false,       // Case insensitive
    searchPosition: 'top'       // Position at top
  },
  pagination: { enabled: true, pageSize: 20 },
  sorting: { enabled: true, multiColumn: true }
};

// Template
<app-smart-grid
  [data]="orders"
  [columnDefs]="columnDefs"
  [config]="gridConfig">
</app-smart-grid>

// Features:
// - Real-time search with debounce (300ms)
// - Searches across all visible columns
// - Case insensitive by default
// - Shows result count
// - Clear button to reset search
// - Combines with column filters and sorting
// - Configurable search fields with searchableFields option`;
}
